import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAllChannels } from '../hooks/useChannel';
import { Loader } from './ui/Loader';
import { EmptyScreen } from './ui/EmptyScreen';

export const SalesChannel: React.FC = () => {
  const { user } = useAuth();
  const { data: channels, isLoading, error } = useAllChannels(user?.id || '');

  if (isLoading) return <Loader text="Fetching sales channels..." />;

  if (error) {
    return <EmptyScreen size="small" title="" description="" />;
  }

  return (
    <div>
      <div className="border-b mb-6">
        <h2 className="text-xl font-semibold mb-1">Sales Channel</h2>
        <p className="text-sm text-gray-600 mb-2">
          Connect and manage your sales channels for seamless order processing.
        </p>
      </div>

      {/* Header */}
      <div className="grid grid-cols-3 bg-teal gap-4 rounded-[10px] px-6 py-3 text-sm text-white font-bold shadow-sm">
        <div>Channel</div>
        <div>Shop Details</div>
        <div className="text-center">Status</div>
      </div>

      {(!channels || channels.length === 0) && (
        <div className="py-12 text-center text-gray-500 border rounded-xl mt-4">
          No sales channels connected yet.
        </div>
      )}

      {channels?.map((channel: any) => {
        const channelType = channel.channeltype || 'woocommerce';
        const iconPath = `/icons/${channelType.toLowerCase()}.svg`;
        const shopName = channel.shopName || '';
        const shopUrl = channel.shopUrl || '';
        const status = channel.status?.toLowerCase() || 'inactive';

        return (
          <div
            key={channel.id}
            className="mt-4 grid grid-cols-3 items-center gap-4 rounded-[16px] border border-gray-100 bg-white px-6 py-5 shadow-sm transition-all hover:shadow-md"
          >
            {/* Channel Icon */}
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 flex items-center justify-center p-2 rounded-xl bg-gray-50 border border-gray-100 overflow-hidden">
                <img
                  src={iconPath}
                  alt={channelType}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const parent = (e.target as HTMLImageElement).parentElement;
                    if (parent) {
                      const span = document.createElement('span');
                      span.className =
                        'text-sm font-bold text-teal flex items-center justify-center';
                      span.innerText = channelType.charAt(0).toUpperCase();
                      parent.appendChild(span);
                    }
                  }}
                />
              </div>
              <span className="font-semibold text-gray-800 capitalize text-sm">
                {channelType}
              </span>
            </div>

            {/* Shop Details */}
            <div className="flex flex-col gap-0.5 min-w-0">
              <div
                className="font-bold text-gray-800 truncate text-sm"
                title={shopName}
              >
                {shopName}
              </div>
              <a
                href={
                  shopUrl.startsWith('http') ? shopUrl : `https://${shopUrl}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-500 hover:text-teal truncate"
                title={shopUrl}
              >
                {shopUrl}
              </a>
            </div>

            {/* Status */}
            <div className="flex justify-center">
              <span
                className={`px-4 py-1.5 rounded-lg text-sm font-bold border-2 ${
                  status === 'active'
                    ? 'bg-green-50 text-green-600 border-green-100'
                    : 'bg-red-50 text-red-600 border-red-100'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
